import { Config, Constant } from '@genial/config';
import { Utils } from '@genial/utils';

import { useToastCreator } from '../../hooks/useToastCreator';
import { ProductType } from '../../pages/ProductsManagement/constants';

import {
  DeleteBatchControlProductPayload,
  DeleteControlProductPayload,
  InsertControlProductPayload,
} from './types';

export const useProductManagementActions = () => {
  const { showToast } = useToastCreator('productManagement');

  const apiUrl = Utils.Normalize.ApiUrl(Config.GenialExtranetApiUrl);

  const insertBatchControlProduct = async ({
    productFilterActionId = 2,
    ...request
  }: InsertControlProductPayload) => {
    let hasError = false;
    const errorTitle = 'Ops! Ocorreu um erro';
    let errorMessage =
      'Tente novamente mais tarde ou entre em contato com time de middle pelo e-mail middleb2b@genial.com.vc.';

    const jwtToken = await Utils.Storage.GetItem(Constant.TokenKey);
    const productList: { idProduto?: number; idEmissorRendaFixa?: number }[] = [];

    if (request.productTypeId === ProductType.FUNDS) {
      request.batchItemsSelected?.forEach((id) => {
        productList.push({ idProduto: Number(id) });
      });
    } else if (request.productTypeId === ProductType.FIXED_INCOME) {
      request.batchItemsSelected?.forEach((id) => {
        if (request.productsList?.length) {
          request.productsList.forEach((product) => {
            productList.push({ idProduto: product.id, idEmissorRendaFixa: Number(id) });
          });
        }
      });
    }

    const params = {
      idProdutoFiltroAcao: productFilterActionId,
      idProdutoFiltroPlataforma: request.productFilterPlatformId,
      idProdutoFiltroTPDestino: request.productFilterDestinationId,
      idDmTipoProduto: request.productTypeId,
      idAssessorDestino: request.advisorId,
      idContaDestino: request.accountId,
      listaProdutos: productList,
    };

    try {
      const response = await fetch(`${apiUrl}/api/Produtos/InserirControleProdutoLote`, {
        method: 'POST',
        body: JSON.stringify(params),
        headers: {
          'content-Type': 'application/json',
          Authorization: `Bearer ${jwtToken}`,
        },
        mode: 'cors',
        credentials: 'same-origin',
      });

      if (response.status !== 200) {
        const body = await response.json();

        hasError = true;
        errorMessage = body.message || errorMessage;
      }
    } catch (_error) {
      hasError = true;
    }

    if (hasError) {
      showToast('error', errorTitle, errorMessage);
      throw new Error();
    }
  };

  const insertFixedIncomeControlProduct = async ({
    productFilterActionId = 2,
    ...request
  }: InsertControlProductPayload) => {
    let hasError = false;
    const errorTitle = 'Ops! Ocorreu um erro';
    let errorMessage =
      'Tente novamente mais tarde ou entre em contato com time de middle pelo e-mail middleb2b@genial.com.vc.';

    const jwtToken = await Utils.Storage.GetItem(Constant.TokenKey);
    const params = {
      idProdutoFiltroAcao: productFilterActionId,
      idProdutoFiltroPlataforma: request.productFilterPlatformId,
      idProdutoFiltroTPDestino: request.productFilterDestinationId,
      idDmTipoProduto: request.productTypeId,
      idAssessorDestino: request.advisorId,
      idContaDestino: request.accountId,
      listaProdutos: request.productsList?.map((item) => ({
        idEmissorRendaFixa: request.productId,
        ...(item.id && { idProduto: item.id }),
      })),
    };

    try {
      const response = await fetch(`${apiUrl}/api/Produtos/InserirControleProdutoLote`, {
        method: 'POST',
        body: JSON.stringify(params),
        headers: {
          'content-Type': 'application/json',
          Authorization: `Bearer ${jwtToken}`,
        },
        mode: 'cors',
        credentials: 'same-origin',
      });

      if (response.status !== 200) {
        const body = await response.json();

        hasError = true;
        errorMessage = body.message || errorMessage;
      }
    } catch (_error) {
      hasError = true;
    }

    if (hasError) {
      showToast('error', errorTitle, errorMessage);
      throw new Error();
    }
  };

  const insertControlProduct = async ({
    productFilterActionId = 2,
    ...request
  }: InsertControlProductPayload) => {
    let hasError = false;
    const errorTitle = 'Ops! Ocorreu um erro';
    let errorMessage =
      'Tente novamente mais tarde ou entre em contato com time de middle pelo e-mail middleb2b@genial.com.vc.';

    const jwtToken = await Utils.Storage.GetItem(Constant.TokenKey);
    const params = {
      idProdutoFiltroAcao: productFilterActionId,
      idProdutoFiltroPlataforma: request.productFilterPlatformId,
      idProdutoFiltroTPDestino: request.productFilterDestinationId,
      idDmTipoProduto: request.productTypeId,
      idProduto: request.productId,
      idEmissorRendaFixa: request.fixedIncomeIssuerId,
      idAssessorDestino: request.advisorId,
      idContaDestino: request.accountId,
      idClienteDestino: request.clientId,
      idProdutoFiltroTPOrigem: request.productFilterOriginId,
      idFilialAssessorDestino: request.branchId,
    };

    try {
      const response = await fetch(`${apiUrl}/api/Produtos/InserirControleProduto`, {
        method: 'POST',
        body: JSON.stringify(params),
        headers: {
          'content-Type': 'application/json',
          Authorization: `Bearer ${jwtToken}`,
        },
        mode: 'cors',
        credentials: 'same-origin',
      });

      if (response.status !== 200) {
        const body = await response.json();

        hasError = true;
        errorMessage = body.message || errorMessage;
      }
    } catch (_error) {
      hasError = true;
    }

    if (hasError) {
      showToast('error', errorTitle, errorMessage);
      throw new Error();
    }
  };

  const deleteControlProduct = async (request: DeleteControlProductPayload) => {
    let hasError = false;
    const errorTitle = 'Ops! Ocorreu um erro';
    let errorMessage =
      'Tente novamente mais tarde ou entre em contato com time de middle pelo e-mail middleb2b@genial.com.vc.';

    const jwtToken = await Utils.Storage.GetItem(Constant.TokenKey);
    const params = {
      idProdutoFiltroInformacao: request.informationProductFilterId,
      tipoProduto: request.productTypeId,
    };

    try {
      const response = await fetch(`${apiUrl}/api/Produtos/DeletarControleProduto`, {
        method: 'POST',
        body: JSON.stringify(params),
        headers: {
          'content-Type': 'application/json',
          Authorization: `Bearer ${jwtToken}`,
        },
        mode: 'cors',
        credentials: 'same-origin',
      });

      if (response.status !== 200) {
        const body = await response.json();

        hasError = true;
        errorMessage = body.message || errorMessage;
      }
    } catch (_error) {
      hasError = true;
    }

    if (hasError) {
      showToast('error', errorTitle, errorMessage);
      throw new Error();
    }
  };

  const deleteBatchControlProduct = async (request: DeleteBatchControlProductPayload) => {
    let hasError = false;
    const errorTitle = 'Ops! Ocorreu um erro';
    let errorMessage =
      'Tente novamente mais tarde ou entre em contato com time de middle pelo e-mail middleb2b@genial.com.vc.';

    const jwtToken = await Utils.Storage.GetItem(Constant.TokenKey);
    const params = {
      idProdutoFiltroAcao: request.actionProductFilterId,
      idDmTipoProduto: request.productTypeId,
      ...(request.fixedIncomeIssuerListId && {
        listaIdEmissorRendaFixa: request.fixedIncomeIssuerListId,
      }),
      ...(request.productListId && {
        listaIdProduto: request.productListId,
        listaIdEmissorRendaFixa: Array(request.productListId.length).fill(0),
      }),
    };

    try {
      const response = await fetch(`${apiUrl}/api/Produtos/DeletarControleProdutoLote`, {
        method: 'POST',
        body: JSON.stringify(params),
        headers: {
          'content-Type': 'application/json',
          Authorization: `Bearer ${jwtToken}`,
        },
        mode: 'cors',
        credentials: 'same-origin',
      });

      if (response.status !== 200) {
        const body = await response.json();

        hasError = true;
        errorMessage = body.message || errorMessage;
      }
    } catch (_error) {
      hasError = true;
    }

    if (hasError) {
      showToast('error', errorTitle, errorMessage);
      throw new Error();
    }
  };

  return {
    insertFixedIncomeControlProduct,
    insertBatchControlProduct,
    insertControlProduct,
    deleteControlProduct,
    deleteBatchControlProduct,
  };
};
